import * as React from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';

import { GlobalToastTypeNames, ToastTypes } from 'types';
import { useToasts } from 'core/hooks';

interface IAiSummaryViewerProps {
  markdown?: string | null;
  model?: string | null;
  lessonUrl?: string;
}

/** Read-only renderer for the AI-generated lesson summary (markdown). */
export const AiSummaryViewer = ({
  markdown,
  model,
  lessonUrl,
}: IAiSummaryViewerProps) => {
  const { t } = useTranslation();
  const { addToast } = useToasts();

  const onCopy = useCallback(() => {
    if (!markdown) {
      return;
    }

    navigator.clipboard.writeText(markdown).then(() => {
      addToast({
        data: {
          type: ToastTypes.success,
          title: `${t('pages.zoom.aiSummary.copied')}`,
        },
        type: GlobalToastTypeNames.ZoomMutation,
      });
    });
  }, [markdown, addToast, t]);

  if (!markdown) {
    return <Empty>{t('pages.zoom.aiSummary.empty')}</Empty>;
  }

  return (
    <Wrapper>
      <Markdown>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
      </Markdown>
      <Footer>
        <Actions>
          <ActionButton type="button" onClick={onCopy}>
            {t('pages.zoom.aiSummary.copy')}
          </ActionButton>
          {lessonUrl ? (
            <ActionLink href={lessonUrl} target="_blank" rel="noreferrer">
              {t('pages.zoom.aiSummary.openLesson')}
            </ActionLink>
          ) : null}
        </Actions>
        {model ? (
          <ModelLabel>{t('pages.zoom.aiSummary.model', { model })}</ModelLabel>
        ) : null}
      </Footer>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 100%;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  box-sizing: border-box;
`;

const Markdown = styled.div`
  font-size: 14px;
  line-height: 22px;
  color: #1f2937;

  h1,
  h2,
  h3 {
    margin: 16px 0 8px;
  }

  pre {
    background: #f4f4f5;
    padding: 12px;
    border-radius: 6px;
    overflow: auto;
  }

  code {
    font-family: monospace;
  }

  blockquote {
    margin: 8px 0;
    padding-left: 12px;
    border-left: 3px solid #d1d5db;
    color: #6b7280;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  flex-wrap: wrap;
  gap: 8px;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
`;

const ActionButton = styled.button`
  border: 1px solid #d1d5db;
  background: #ffffff;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 13px;
  cursor: pointer;

  &:hover {
    background: #f9fafb;
  }
`;

const ActionLink = styled.a`
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 13px;
  color: #2563eb;
  text-decoration: none;

  &:hover {
    background: #f9fafb;
  }
`;

const ModelLabel = styled.span`
  font-size: 12px;
  color: #5b6775;
`;

const Empty = styled.div`
  padding: 24px;
  text-align: center;
  color: #5b6775;
  font-size: 14px;
  border: 1px dashed #e5e7eb;
  border-radius: 8px;
`;
